import { useTranslation } from 'react-i18next';
import { formatPt } from '../../../lib/format.js';
import { supportsRequirementView, type DevelopPointView } from './develop-point-view.js';
import type { KpiTeam, KpiWeeklyTrendPoint } from '../../../types/api.js';

interface WeeklyPointTableProps {
  data: KpiWeeklyTrendPoint[];
  onWeekClick?: (weekStart: string, weekEnd: string) => void;
  /**
   * 親の team フィルタ。要件定義pt 行の表示可否に使う。
   * 要件定義工程は PDG 側にしか無いため、ACE / 全体スコープでは行を出さない（常に "-" になる）。
   */
  team?: KpiTeam | 'all';
  /**
   * 開発pt の口径（開発pt (MTD) カードと共有）。要件定義ビュー非対応スコープでは「全部」扱い。
   *  - all: 実績pt / 目標pt / 達成率 は要件定義込み（従来どおり）
   *  - excludeRequirement: 実績pt / 目標pt / 達成率 を要件定義を除いた値に差し替える
   *  - breakdown: 要件定義抜きに加えて 目標pt（要件定義）行を出し、要件定義pt 行と対にする
   */
  view?: DevelopPointView;
}

/** weekStart (YYYY-MM-DD, 月曜) → "M/D" 表示 @graph-connects none */
function formatWeekLabel(weekStart: string): string {
  const parts = weekStart.split('-');
  if (parts.length !== 3) return weekStart;
  return `${String(Number(parts[1]))}/${String(Number(parts[2]))}`;
}

/**
 * 達成率バッジの色を決める（達成率 / 達成率(個人積み上げ) 共通）。
 *  - 当週 (today が weekStart..weekEnd に入っている週) は値に関わらず奶黄色
 *    (進行中なので評価対象外)
 *  - 100% 以上 → 浅緑、未満 → ピンク
 * @graph-connects none
 */
export function combatPowerAchievementBadgeClass(rate: number, isCurrentWeek: boolean): string {
  if (isCurrentWeek) {
    return 'bg-[#FFF4C2] text-[#8A6D1E] border border-[#F1D784]';
  }
  if (rate >= 100) {
    return 'bg-[#DCFCE7] text-[#15803D] border border-[#BBF7D0]';
  }
  // 未達ピンクの背景・枠線はテーマ変数で切替（styles.css）:
  //  ライトは従来どおり半透明、ダークは不透明にして緑/黄パステルと視認性を揃える
  return 'bg-[var(--color-rate-below-bg)] text-[#BE185D] border border-[var(--color-rate-below-border)]';
}

/**
 * 今日 (JST 基準 YYYY-MM-DD) が weekStart..weekEnd に含まれるか判定する。
 *  - row.weekStart/weekEnd は API が JST 月曜起点で返す YYYY-MM-DD 文字列
 *  - ブラウザのローカルタイムゾーンが JST 以外だと跨日タイミングで「当週」判定が
 *    1 日ズレるため、Intl.DateTimeFormat で Asia/Tokyo に固定する
 * @graph-connects none
 */
function isCurrentWeekRow(row: KpiWeeklyTrendPoint): boolean {
  // 'en-CA' ロケールは YYYY-MM-DD 形式を返すので ISO 形式と一致
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Tokyo' }).format(new Date());
  return row.weekStart <= today && today <= row.weekEnd;
}

/** @graph-connects none */
const ROW_HEADER_CLASS =
  'sticky left-0 z-10 bg-[var(--color-bg-surface)] text-left py-2 px-3 font-medium text-[var(--color-text-secondary)] whitespace-nowrap';
/** @graph-connects none */
const PRIMARY_CELL_CLASS =
  'py-2 px-3 text-[var(--color-text-primary)] text-right whitespace-nowrap';
/** @graph-connects none */
const SECONDARY_CELL_CLASS =
  'py-2 px-3 text-[var(--color-text-secondary)] text-right whitespace-nowrap';

/**
 * 週次pt集計の転置表（週が列、メトリクスが行）
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 週次pt集計データの転置表表示。WeeklyPointTrendChart から分離して 1 ファイル 500 行制限を遵守。PDG 系スコープでは「実績±処理中増減pt」の次に「要件定義pt」行（要件定義セット子票の実績pt）を併記する。開発pt の口径切替（全部 / 要件定義抜き / 内訳）に応じて 実績pt・実績±処理中増減pt・目標pt・達成率・月内累計（実績 / 処理中含 / 目標 / 達成率）行を要件定義を除いた値に差し替え、内訳では 目標pt（要件定義）行を足す。個人積み上げ（キャパ）の 目標pt・達成率・月内累計目標 行は要件定義ビューでは「個人積み上げ − 要件定義予算」の値に差し替え、要件定義と分けられない処理中pt は注記で要件定義込みと明示する
 * @graph-connects supportsRequirementView [calls] team スコープが要件定義pt 行の対象かを判定
 * @graph-connects none
 */
export function WeeklyPointTable({ data, onWeekClick, team, view = 'all' }: WeeklyPointTableProps) {
  const { t } = useTranslation();
  const showRequirementRow = supportsRequirementView(team);
  // 要件定義ビュー非対応スコープ（ACE 系）は口径を切り替えず従来表示
  const effectiveView: DevelopPointView = showRequirementRow ? view : 'all';
  const excludeRequirement = effectiveView !== 'all';
  const actualOf = (row: KpiWeeklyTrendPoint) =>
    excludeRequirement ? row.developActualExcludingRequirement : row.developActual;
  const targetOf = (row: KpiWeeklyTrendPoint) =>
    excludeRequirement ? row.developTargetExcludingRequirement : row.developTarget;
  const achievementRateOf = (row: KpiWeeklyTrendPoint) =>
    excludeRequirement ? row.achievementRateExcludingRequirement : row.achievementRate;
  // 出来高・月内累計も口径に合わせる。処理中pt は要件定義と分けられないので口径によらず同じ値
  const outputPtOf = (row: KpiWeeklyTrendPoint) =>
    excludeRequirement ? row.developOutputPtExcludingRequirement : row.developOutputPt;
  const cumulativeActualOf = (row: KpiWeeklyTrendPoint) =>
    excludeRequirement
      ? row.cumulativeDevelopActualExcludingRequirement
      : row.cumulativeDevelopActual;
  const cumulativeActualWithWipOf = (row: KpiWeeklyTrendPoint) =>
    excludeRequirement
      ? row.cumulativeDevelopActualWithWipExcludingRequirement
      : row.cumulativeDevelopActualWithWip;
  const cumulativeTargetOf = (row: KpiWeeklyTrendPoint) =>
    excludeRequirement
      ? row.cumulativeDevelopTargetExcludingRequirement
      : row.cumulativeDevelopTarget;
  const cumulativeAchievementRateOf = (row: KpiWeeklyTrendPoint) =>
    excludeRequirement
      ? row.cumulativeAchievementRateExcludingRequirement
      : row.cumulativeAchievementRate;
  // 個人積み上げは要件定義ビューでは「個人積み上げ − 要件定義予算」
  const targetCombatPowerOf = (row: KpiWeeklyTrendPoint) =>
    excludeRequirement
      ? row.developTargetCombatPowerExcludingRequirement
      : row.developTargetCombatPower;
  const achievementRateCombatPowerOf = (row: KpiWeeklyTrendPoint) =>
    excludeRequirement
      ? row.achievementRateCombatPowerExcludingRequirement
      : row.achievementRateCombatPower;
  const cumulativeTargetCombatPowerOf = (row: KpiWeeklyTrendPoint) =>
    excludeRequirement
      ? row.cumulativeDevelopTargetCombatPowerExcludingRequirement
      : row.cumulativeDevelopTargetCombatPower;
  // 要件定義ビューのラベル（全部ビューの i18n キーに ExcludingRequirement を付けたキー）
  const rowLabel = (key: string) => t(excludeRequirement ? `${key}ExcludingRequirement` : key);
  return (
    <div className="mt-4 overflow-x-auto" data-testid="weekly-point-table">
      <table className="text-sm border-collapse">
        <thead>
          <tr className="border-b border-[var(--color-border)]">
            <th className="sticky left-0 z-10 bg-[var(--color-bg-surface)] text-left py-2 px-3 font-medium text-[var(--color-text-muted)] whitespace-nowrap min-w-[140px]">
              {t('kpi.weeklyTable.metric')}
            </th>
            {data.map((row) =>
              onWeekClick ? (
                <th
                  key={row.weekStart}
                  className="text-right py-2 px-3 font-medium text-[var(--color-text-muted)] whitespace-nowrap"
                >
                  <button
                    type="button"
                    onClick={() => {
                      onWeekClick(row.weekStart, row.weekEnd);
                    }}
                    className="text-[var(--color-accent-primary)] hover:underline"
                    aria-label={t('kpi.weeklyDetail.openLabel', {
                      weekStart: row.weekStart,
                      weekEnd: row.weekEnd,
                    })}
                  >
                    {formatWeekLabel(row.weekStart)}
                  </button>
                </th>
              ) : (
                <th
                  key={row.weekStart}
                  className="text-right py-2 px-3 font-medium text-[var(--color-text-muted)] whitespace-nowrap"
                >
                  {formatWeekLabel(row.weekStart)}
                </th>
              ),
            )}
          </tr>
        </thead>
        <tbody>
          <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]">
            <th scope="row" className={ROW_HEADER_CLASS}>
              {excludeRequirement
                ? t('kpi.weeklyActualExcludingRequirement')
                : t('kpi.weeklyActual')}
            </th>
            {data.map((row) => {
              const actual = actualOf(row);
              return (
                <td key={row.weekStart} className={PRIMARY_CELL_CLASS}>
                  {actual != null ? formatPt(actual) : '-'}
                </td>
              );
            })}
          </tr>
          <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]">
            <th scope="row" className={ROW_HEADER_CLASS}>
              {t('kpi.weeklyTable.inProgress')}
            </th>
            {data.map((row) => (
              <td key={row.weekStart} className={SECONDARY_CELL_CLASS}>
                {row.inProgressPt != null ? formatPt(row.inProgressPt) : '-'}
              </td>
            ))}
          </tr>
          <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]">
            <th scope="row" className={ROW_HEADER_CLASS}>
              {rowLabel('kpi.weeklyTable.outputPt')}
            </th>
            {data.map((row) => {
              const outputPt = outputPtOf(row);
              return (
                <td key={row.weekStart} className={PRIMARY_CELL_CLASS}>
                  {outputPt != null ? formatPt(outputPt) : '-'}
                </td>
              );
            })}
          </tr>
          {showRequirementRow && (
            <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]">
              <th scope="row" className={ROW_HEADER_CLASS}>
                {t('kpi.weeklyTable.requirementDefinition')}
              </th>
              {data.map((row) => (
                <td key={row.weekStart} className={SECONDARY_CELL_CLASS}>
                  {row.requirementDefinitionActual != null
                    ? formatPt(row.requirementDefinitionActual)
                    : '-'}
                </td>
              ))}
            </tr>
          )}
          <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]">
            <th scope="row" className={ROW_HEADER_CLASS}>
              {excludeRequirement
                ? t('kpi.weeklyTargetExcludingRequirement')
                : t('kpi.weeklyTarget')}
            </th>
            {data.map((row) => {
              const target = targetOf(row);
              return (
                <td key={row.weekStart} className={PRIMARY_CELL_CLASS}>
                  {target != null ? formatPt(target) : '-'}
                </td>
              );
            })}
          </tr>
          {effectiveView === 'breakdown' && (
            <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]">
              <th scope="row" className={ROW_HEADER_CLASS}>
                {t('kpi.weeklyRequirementTarget')}
              </th>
              {data.map((row) => (
                <td key={row.weekStart} className={SECONDARY_CELL_CLASS}>
                  {row.requirementDefinitionTarget != null
                    ? formatPt(row.requirementDefinitionTarget)
                    : '-'}
                </td>
              ))}
            </tr>
          )}
          <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]">
            <th scope="row" className={ROW_HEADER_CLASS}>
              {rowLabel('kpi.weeklyTargetCombatPower')}
            </th>
            {data.map((row) => {
              const value = targetCombatPowerOf(row);
              return (
                <td key={row.weekStart} className={PRIMARY_CELL_CLASS}>
                  {value != null ? formatPt(value) : '-'}
                </td>
              );
            })}
          </tr>
          <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]">
            <th scope="row" className={ROW_HEADER_CLASS}>
              {excludeRequirement
                ? t('kpi.achievementRateExcludingRequirement')
                : t('kpi.achievementRate')}
            </th>
            {data.map((row) => {
              const rate = achievementRateOf(row);
              return (
                <td key={row.weekStart} className={PRIMARY_CELL_CLASS}>
                  {rate != null ? (
                    <span
                      className={`inline-flex items-center justify-center font-medium rounded-md px-1.5 py-0.5 text-[11px] leading-none ${combatPowerAchievementBadgeClass(rate, isCurrentWeekRow(row))}`}
                    >
                      {`${String(rate)}%`}
                    </span>
                  ) : (
                    '-'
                  )}
                </td>
              );
            })}
          </tr>
          <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]">
            <th scope="row" className={ROW_HEADER_CLASS}>
              {rowLabel('kpi.achievementRateCombatPower')}
            </th>
            {data.map((row) => {
              const rate = achievementRateCombatPowerOf(row);
              return (
                <td key={row.weekStart} className={PRIMARY_CELL_CLASS}>
                  {rate != null ? (
                    <span
                      className={`inline-flex items-center justify-center font-medium rounded-md px-1.5 py-0.5 text-[11px] leading-none ${combatPowerAchievementBadgeClass(rate, isCurrentWeekRow(row))}`}
                    >
                      {`${String(rate)}%`}
                    </span>
                  ) : (
                    '-'
                  )}
                </td>
              );
            })}
          </tr>
          <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]">
            <th scope="row" className={ROW_HEADER_CLASS}>
              {rowLabel('kpi.weeklyTable.cumulativeActual')}
            </th>
            {data.map((row) => {
              const value = cumulativeActualOf(row);
              return (
                <td key={row.weekStart} className={SECONDARY_CELL_CLASS}>
                  {value != null ? formatPt(value) : '-'}
                </td>
              );
            })}
          </tr>
          <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]">
            <th scope="row" className={ROW_HEADER_CLASS}>
              {rowLabel('kpi.weeklyTable.cumulativeActualWithInProgress')}
            </th>
            {data.map((row) => {
              const value = cumulativeActualWithWipOf(row);
              return (
                <td key={row.weekStart} className={SECONDARY_CELL_CLASS}>
                  {value != null ? formatPt(value) : '-'}
                </td>
              );
            })}
          </tr>
          <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]">
            <th scope="row" className={ROW_HEADER_CLASS}>
              {rowLabel('kpi.weeklyTable.cumulativeTarget')}
            </th>
            {data.map((row) => {
              const value = cumulativeTargetOf(row);
              return (
                <td key={row.weekStart} className={SECONDARY_CELL_CLASS}>
                  {value != null ? formatPt(value) : '-'}
                </td>
              );
            })}
          </tr>
          <tr className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-hover)]">
            <th scope="row" className={ROW_HEADER_CLASS}>
              {rowLabel('kpi.weeklyTable.cumulativeTargetCombatPower')}
            </th>
            {data.map((row) => {
              const value = cumulativeTargetCombatPowerOf(row);
              return (
                <td key={row.weekStart} className={SECONDARY_CELL_CLASS}>
                  {value != null ? formatPt(value) : '-'}
                </td>
              );
            })}
          </tr>
          <tr className="hover:bg-[var(--color-bg-hover)]">
            <th scope="row" className={ROW_HEADER_CLASS}>
              {rowLabel('kpi.weeklyTable.cumulativeAchievementRate')}
            </th>
            {data.map((row) => {
              const rate = cumulativeAchievementRateOf(row);
              return (
                <td key={row.weekStart} className={PRIMARY_CELL_CLASS}>
                  {rate != null ? (
                    <span
                      className={`inline-flex items-center justify-center font-medium rounded-md px-1.5 py-0.5 text-[11px] leading-none ${combatPowerAchievementBadgeClass(rate, isCurrentWeekRow(row))}`}
                    >
                      {`${String(rate)}%`}
                    </span>
                  ) : (
                    '-'
                  )}
                </td>
              );
            })}
          </tr>
        </tbody>
      </table>
      {excludeRequirement && (
        <p className="mt-2 text-xs text-[var(--color-text-muted)]">
          {t('kpi.weeklyTable.requirementViewNote')}
        </p>
      )}
    </div>
  );
}
